(() => {
  const STORAGE_KEY = 'carbonLivingAffiliateRef';

  function parseJson(root, selector, fallback = {}) {
    const node = root.querySelector(selector);
    if (!node) return fallback;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('[Affiliate Builder] JSON invalide', error);
      return fallback;
    }
  }

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-CA', {
        style: 'currency',
        currency: currency || 'CAD'
      }).format((Number(cents) || 0) / 100);
    } catch (error) {
      return `${((Number(cents) || 0) / 100).toFixed(2)} $`;
    }
  }

  function escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function extractScaRef(value) {
    if (!value) return '';

    const raw = String(value).trim();

    try {
      const url = new URL(raw, window.location.origin);
      const ref = url.searchParams.get('sca_ref');
      if (ref) return ref.trim();
    } catch (error) {}

    if (/^[A-Za-z0-9._-]+$/.test(raw) && !raw.includes('/')) {
      return raw;
    }

    return '';
  }

  function extractProductHandle(value) {
    const raw = String(value || '').trim();
    if (!raw) throw new Error('Aucun produit sélectionné.');

    const url = new URL(raw, window.location.origin);
    const match = url.pathname.match(/\/products\/([^/?#]+)/i);

    if (!match) {
      throw new Error('Impossible d’identifier ce produit Shopify.');
    }

    return {
      handle: decodeURIComponent(match[1]),
      requestedVariantId: url.searchParams.get('variant') || ''
    };
  }

  function getShopifyRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function debounce(fn, delay = 250) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  }

  function initBuilder(root) {
    if (!root || root.dataset.builderReady === 'true') return;
    root.dataset.builderReady = 'true';

    const config = parseJson(root, '[data-builder-config]', {});
    const currency = config.currency || 'CAD';
    const predictiveLimit = Number(config.predictive_limit || 8);
    const minSearchChars = Number(config.min_search_chars || 2);

    const affiliateInput = root.querySelector('[data-affiliate-input]');
    const validateRefButton = root.querySelector('[data-validate-ref]');
    const refStatus = root.querySelector('[data-ref-status]');

    const productSearchInput = root.querySelector('[data-product-search]');
    const searchResults = root.querySelector('[data-search-results]');
    const searchLoader = root.querySelector('[data-search-loader]');

    const productUrlInput = root.querySelector('[data-product-url]');
    const addProductUrlButton = root.querySelector('[data-add-product-url]');

    const productStatus = root.querySelector('[data-product-status]');
    const selectionLimit = root.querySelector('[data-selection-limit]');
    const selectionEmpty = root.querySelector('[data-selection-empty]');
    const selectionGrid = root.querySelector('[data-selection-grid]');

    const summaryCount = root.querySelector('[data-summary-count]');
    const summaryTotal = root.querySelector('[data-summary-total]');
    const generateButton = root.querySelector('[data-generate-link]');

    const result = root.querySelector('[data-result]');
    const generatedLink = root.querySelector('[data-generated-link]');
    const copyButton = root.querySelector('[data-copy-link]');
    const copyStatus = root.querySelector('[data-copy-status]');
    const testLink = root.querySelector('[data-test-link]');

    const mobileBar = root.querySelector('[data-mobile-bar]');
    const mobileCount = root.querySelector('[data-mobile-count]');
    const mobileTotal = root.querySelector('[data-mobile-total]');
    const scrollSummary = root.querySelector('[data-scroll-summary]');
    const summaryStep = root.querySelector('[data-step="summary"]');

    const selection = new Map();

    let scaRef = '';
    let searchAbortController = null;
    let activeSearchIndex = -1;
    let latestSearchProducts = [];

    function setStatus(node, message, type = '') {
      if (!node) return;
      node.textContent = message;
      node.classList.remove('is-success', 'is-error');
      if (type) node.classList.add(`is-${type}`);
    }

    function setSearchLoading(isLoading) {
      if (searchLoader) searchLoader.hidden = !isLoading;
    }

    function openSearchResults() {
      if (!searchResults) return;
      searchResults.hidden = false;
      productSearchInput?.setAttribute('aria-expanded', 'true');
    }

    function closeSearchResults() {
      if (!searchResults) return;
      searchResults.hidden = true;
      productSearchInput?.setAttribute('aria-expanded', 'false');
      activeSearchIndex = -1;
    }

    function saveRef(ref) {
      scaRef = ref;

      try {
        localStorage.setItem(STORAGE_KEY, ref);
      } catch (error) {}

      if (affiliateInput) {
        affiliateInput.value = `${window.location.origin}?sca_ref=${ref}`;
      }

      setStatus(
        refStatus,
        '✓ Lien ambassadrice reconnu. Ton identifiant sera conservé dans le lien final.',
        'success'
      );

      updateUI();
    }

    function resolveInitialRef() {
      const queryRef = new URLSearchParams(window.location.search).get('sca_ref');

      if (queryRef) {
        saveRef(queryRef);
        return;
      }

      const defaultRef = root.dataset.defaultRef || '';

      if (defaultRef) {
        saveRef(defaultRef);
        return;
      }

      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) saveRef(stored);
      } catch (error) {}
    }

    function validateAffiliateInput() {
      const ref = extractScaRef(affiliateInput?.value || '');

      if (!ref) {
        scaRef = '';
        setStatus(refStatus, 'Le lien ne contient pas de paramètre sca_ref valide.', 'error');
        updateUI();
        return false;
      }

      saveRef(ref);
      return true;
    }

    function getSelectedVariant(entry) {
      return entry.product.variants.find(
        variant => String(variant.id) === String(entry.variantId)
      );
    }

    async function fetchProductByHandle(handle) {
      const rootPath = getShopifyRoot();
      const endpoint = `${rootPath}products/${encodeURIComponent(handle)}.js`;

      const response = await fetch(endpoint, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Impossible de charger ce produit. Vérifie qu’il est publié sur la boutique.');
      }

      return response.json();
    }

    async function addProductByUrl(url, requestedVariantId = '') {
      const normalized = extractProductHandle(url);

      if (requestedVariantId) {
        normalized.requestedVariantId = requestedVariantId;
      }

      const product = await fetchProductByHandle(normalized.handle);

      if (!Array.isArray(product.variants) || product.variants.length === 0) {
        throw new Error('Aucune variante Shopify trouvée pour ce produit.');
      }

      const duplicateProduct = [...selection.values()].find(
        item => String(item.product.id) === String(product.id)
      );

      if (duplicateProduct) {
        throw new Error('Ce produit est déjà dans ta sélection.');
      }

      let variant = null;

      if (normalized.requestedVariantId) {
        variant = product.variants.find(
          item => String(item.id) === String(normalized.requestedVariantId)
        );
      }

      if (!variant) {
        variant = product.variants.find(item => item.available);
      }

      if (!variant) {
        throw new Error('Ce produit ne possède aucune variante disponible.');
      }

      const key = `${product.id}:${variant.id}`;

      selection.set(key, {
        key,
        product,
        variantId: String(variant.id),
        quantity: 1
      });

      updateUI();
      return product;
    }

    function createProductCard(entry) {
      const product = entry.product;
      const variant = getSelectedVariant(entry);

      const card = document.createElement('article');
      card.className = 'cl-affiliate-product';
      card.dataset.selectionKey = entry.key;

      const imageUrl =
        product.featured_image ||
        (Array.isArray(product.images) && product.images[0]) ||
        '';

      const imageHtml = imageUrl
        ? `<img class="cl-affiliate-product__image" src="${escapeHtml(imageUrl)}" alt="">`
        : '<div class="cl-affiliate-product__image"></div>';

      const hasMultipleVariants = product.variants.length > 1;

      const variantOptions = hasMultipleVariants
        ? `
          <div class="cl-affiliate-product__variant-wrap">
            <label class="cl-affiliate-product__variant-label">Variante</label>
            <select class="cl-affiliate-product__variant-select" data-card-variant>
              ${product.variants.map(v => `
                <option
                  value="${v.id}"
                  ${String(v.id) === String(entry.variantId) ? 'selected' : ''}
                  ${!v.available ? 'disabled' : ''}
                >
                  ${escapeHtml(v.title)}${!v.available ? ' — Épuisé' : ''}
                </option>
              `).join('')}
            </select>
          </div>
        `
        : '';

      card.innerHTML = `
        <div class="cl-affiliate-product__media">
          ${imageHtml}
        </div>

        <div class="cl-affiliate-product__body">
          <h3 class="cl-affiliate-product__title">${escapeHtml(product.title)}</h3>

          <div class="cl-affiliate-product__price">
            ${formatMoney(variant?.price || 0, currency)}
          </div>

          ${variantOptions}

          <div class="cl-affiliate-product__controls">
            <div class="cl-affiliate-product__quantity">
              <button type="button" aria-label="Diminuer la quantité" data-card-decrease>−</button>
              <span data-card-quantity>${entry.quantity}</span>
              <button type="button" aria-label="Augmenter la quantité" data-card-increase>+</button>
            </div>

            <button type="button" class="cl-affiliate-product__remove" data-card-remove>
              Retirer
            </button>
          </div>
        </div>
      `;

      card.querySelector('[data-card-decrease]')?.addEventListener('click', () => {
        entry.quantity -= 1;

        if (entry.quantity <= 0) {
          selection.delete(entry.key);
        }

        updateUI();
      });

      card.querySelector('[data-card-increase]')?.addEventListener('click', () => {
        entry.quantity += 1;
        updateUI();
      });

      card.querySelector('[data-card-remove]')?.addEventListener('click', () => {
        selection.delete(entry.key);
        updateUI();
      });

      card.querySelector('[data-card-variant]')?.addEventListener('change', event => {
        const newVariantId = String(event.target.value);
        const oldKey = entry.key;
        const newKey = `${product.id}:${newVariantId}`;

        selection.delete(oldKey);
        entry.variantId = newVariantId;
        entry.key = newKey;
        selection.set(newKey, entry);

        updateUI();
      });

      return card;
    }

    function renderSelection() {
      if (!selectionGrid) return;

      selectionGrid.innerHTML = '';
      const entries = [...selection.values()];

      entries.forEach(entry => {
        selectionGrid.appendChild(createProductCard(entry));
      });

      if (selectionEmpty) {
        selectionEmpty.hidden = entries.length > 0;
      }
    }

    function updateTotals() {
      const entries = [...selection.values()];

      const totalItems = entries.reduce(
        (sum, entry) => sum + entry.quantity,
        0
      );

      const totalPrice = entries.reduce((sum, entry) => {
        const variant = getSelectedVariant(entry);
        return sum + (Number(variant?.price || 0) * entry.quantity);
      }, 0);

      if (summaryCount) summaryCount.textContent = String(totalItems);
      if (summaryTotal) summaryTotal.textContent = formatMoney(totalPrice, currency);

      if (selectionLimit) {
        const count = entries.length;
        selectionLimit.textContent =
          `${count} produit${count > 1 ? 's' : ''} ajouté${count > 1 ? 's' : ''}`;
      }

      if (mobileCount) {
        mobileCount.textContent =
          `${totalItems} produit${totalItems > 1 ? 's' : ''}`;
      }

      if (mobileTotal) {
        mobileTotal.textContent = formatMoney(totalPrice, currency);
      }

      if (mobileBar) {
        mobileBar.hidden = entries.length === 0;
      }
    }

    function updateGenerateState() {
      if (!generateButton) return;
      generateButton.disabled = selection.size === 0 || !scaRef;
    }

    function invalidateGeneratedLink() {
      if (result) result.hidden = true;
      if (generatedLink) generatedLink.value = '';
      if (testLink) testLink.href = '#';

      if (copyStatus) {
        copyStatus.textContent = '';
        copyStatus.classList.remove('is-success', 'is-error');
      }
    }

    function updateUI() {
      renderSelection();
      updateTotals();
      updateGenerateState();
      invalidateGeneratedLink();
    }

    function generatePermalink() {
      if (!scaRef) {
        setStatus(
          refStatus,
          'Ajoute d’abord ton lien ambassadrice UpPromote.',
          'error'
        );
        affiliateInput?.focus();
        return;
      }

      if (selection.size === 0) {
        setStatus(
          productStatus,
          'Ajoute au moins un produit à ta sélection.',
          'error'
        );
        return;
      }

      const cartItems = [...selection.values()]
        .map(entry => `${entry.variantId}:${entry.quantity}`)
        .join(',');

      const storeOrigin =
        (root.dataset.storeOrigin || window.location.origin).replace(/\/$/, '');

      const url = new URL(`${storeOrigin}/cart/${cartItems}`);
      url.searchParams.set('storefront', 'true');
      url.searchParams.set('sca_ref', scaRef);

      if (generatedLink) generatedLink.value = url.toString();
      if (testLink) testLink.href = url.toString();
      if (result) result.hidden = false;

      result?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
    }

    function getPredictiveImage(product) {
      if (!product) return '';

      if (product.image) {
        if (typeof product.image === 'string') return product.image;
        if (product.image.url) return product.image.url;
        if (product.image.src) return product.image.src;
      }

      if (product.featured_image) {
        if (typeof product.featured_image === 'string') return product.featured_image;
        if (product.featured_image.url) return product.featured_image.url;
      }

      return '';
    }

    function getPredictivePrice(product) {
      if (!product) return '';

      const raw = product.price;

      if (typeof raw === 'number') {
        return formatMoney(raw * 100, currency);
      }

      if (typeof raw === 'string') {
        const n = Number(raw);
        if (Number.isFinite(n)) {
          return new Intl.NumberFormat(document.documentElement.lang || 'fr-CA', {
            style: 'currency',
            currency
          }).format(n);
        }
      }

      if (raw && raw.amount) {
        const n = Number(raw.amount);
        if (Number.isFinite(n)) {
          return new Intl.NumberFormat(document.documentElement.lang || 'fr-CA', {
            style: 'currency',
            currency
          }).format(n);
        }
      }

      return '';
    }

    function renderPredictiveResults(products) {
      if (!searchResults) return;

      latestSearchProducts = products || [];
      activeSearchIndex = -1;
      searchResults.innerHTML = '';

      if (!latestSearchProducts.length) {
        searchResults.innerHTML =
          '<div class="cl-affiliate-builder__search-empty">Aucun produit trouvé.</div>';
        openSearchResults();
        return;
      }

      latestSearchProducts.forEach((product, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'cl-affiliate-search-result';
        button.setAttribute('role', 'option');
        button.dataset.searchIndex = String(index);

        const imageUrl = getPredictiveImage(product);
        const imageHtml = imageUrl
          ? `<img class="cl-affiliate-search-result__image" src="${escapeHtml(imageUrl)}" alt="">`
          : '<div class="cl-affiliate-search-result__image"></div>';

        button.innerHTML = `
          ${imageHtml}
          <span class="cl-affiliate-search-result__info">
            <span class="cl-affiliate-search-result__title">${escapeHtml(product.title || '')}</span>
            <span class="cl-affiliate-search-result__type">${escapeHtml(product.type || product.product_type || '')}</span>
          </span>
          <span class="cl-affiliate-search-result__price">${escapeHtml(getPredictivePrice(product))}</span>
        `;

        button.addEventListener('mousedown', event => event.preventDefault());
        button.addEventListener('click', () => selectPredictiveProduct(product));

        searchResults.appendChild(button);
      });

      openSearchResults();
    }

    async function selectPredictiveProduct(product) {
      if (!product || !product.url) return;

      closeSearchResults();
      if (productSearchInput) productSearchInput.value = '';

      setStatus(productStatus, 'Ajout du produit...');

      try {
        const addedProduct = await addProductByUrl(product.url);

        setStatus(
          productStatus,
          `✓ ${addedProduct.title} a été ajouté à ta sélection.`,
          'success'
        );
      } catch (error) {
        setStatus(
          productStatus,
          error.message || 'Impossible d’ajouter ce produit.',
          'error'
        );
      }
    }

    async function performPredictiveSearch(query) {
      const value = String(query || '').trim();

      if (value.length < minSearchChars) {
        closeSearchResults();
        if (searchResults) searchResults.innerHTML = '';
        return;
      }

      if (searchAbortController) {
        searchAbortController.abort();
      }

      searchAbortController = new AbortController();
      setSearchLoading(true);

      try {
        const rootPath = getShopifyRoot();

        const params = new URLSearchParams();
        params.set('q', value);
        params.set('resources[type]', 'product');
        params.set('resources[limit]', String(predictiveLimit));
        params.set('resources[options][unavailable_products]', 'hide');

        const response = await fetch(
          `${rootPath}search/suggest.json?${params.toString()}`,
          {
            signal: searchAbortController.signal,
            credentials: 'same-origin',
            headers: { Accept: 'application/json' }
          }
        );

        if (!response.ok) {
          throw new Error(`Recherche Shopify indisponible (${response.status}).`);
        }

        const data = await response.json();

        const products =
          data?.resources?.results?.products ||
          data?.resources?.products ||
          [];

        renderPredictiveResults(products);

      } catch (error) {
        if (error.name === 'AbortError') return;

        if (searchResults) {
          searchResults.innerHTML =
            '<div class="cl-affiliate-builder__search-empty">La recherche est temporairement indisponible. Utilise l’URL produit ci-dessous.</div>';
        }

        openSearchResults();
      } finally {
        setSearchLoading(false);
      }
    }

    function updateActiveSearchOption() {
      if (!searchResults) return;

      const options = [...searchResults.querySelectorAll('.cl-affiliate-search-result')];

      options.forEach((option, index) => {
        const active = index === activeSearchIndex;
        option.classList.toggle('is-active', active);
        option.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) option.scrollIntoView({ block: 'nearest' });
      });
    }

    const debouncedSearch = debounce(value => performPredictiveSearch(value), 250);

    productSearchInput?.addEventListener('input', event => {
      debouncedSearch(event.target.value);
    });

    productSearchInput?.addEventListener('focus', () => {
      if (latestSearchProducts.length) openSearchResults();
    });

    productSearchInput?.addEventListener('keydown', event => {
      const options = [...searchResults?.querySelectorAll('.cl-affiliate-search-result') || []];
      if (!options.length) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        activeSearchIndex = Math.min(activeSearchIndex + 1, options.length - 1);
        updateActiveSearchOption();
      }

      if (event.key === 'ArrowUp') {
        event.preventDefault();
        activeSearchIndex = Math.max(activeSearchIndex - 1, 0);
        updateActiveSearchOption();
      }

      if (event.key === 'Enter' && activeSearchIndex >= 0) {
        event.preventDefault();
        const product = latestSearchProducts[activeSearchIndex];
        if (product) selectPredictiveProduct(product);
      }

      if (event.key === 'Escape') {
        closeSearchResults();
      }
    });

    document.addEventListener('click', event => {
      if (!root.contains(event.target)) return;
      if (!event.target.closest('[data-autocomplete]')) {
        closeSearchResults();
      }
    });

    validateRefButton?.addEventListener('click', validateAffiliateInput);
    affiliateInput?.addEventListener('change', validateAffiliateInput);

    addProductUrlButton?.addEventListener('click', async () => {
      const url = productUrlInput?.value?.trim();

      if (!url) {
        setStatus(productStatus, 'Colle une URL produit.', 'error');
        return;
      }

      addProductUrlButton.disabled = true;
      setStatus(productStatus, 'Ajout du produit...');

      try {
        const product = await addProductByUrl(url);
        if (productUrlInput) productUrlInput.value = '';

        setStatus(
          productStatus,
          `✓ ${product.title} a été ajouté à ta sélection.`,
          'success'
        );
      } catch (error) {
        setStatus(
          productStatus,
          error.message || 'Impossible d’ajouter ce produit.',
          'error'
        );
      } finally {
        addProductUrlButton.disabled = false;
      }
    });

    productUrlInput?.addEventListener('keydown', event => {
      if (event.key === 'Enter') {
        event.preventDefault();
        addProductUrlButton?.click();
      }
    });

    generateButton?.addEventListener('click', generatePermalink);

    copyButton?.addEventListener('click', async () => {
      if (!generatedLink?.value) return;

      try {
        await navigator.clipboard.writeText(generatedLink.value);
      } catch (error) {
        generatedLink.select();
        document.execCommand('copy');
      }

      setStatus(copyStatus, '✓ Lien copié.', 'success');
    });

    scrollSummary?.addEventListener('click', () => {
      summaryStep?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });

    resolveInitialRef();
    updateUI();
  }

  function initAll() {
    document
      .querySelectorAll('[data-affiliate-builder]')
      .forEach(initBuilder);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', event => {
    event.target
      .querySelectorAll('[data-affiliate-builder]')
      .forEach(initBuilder);
  });
})();
